import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'

import {
  type CommentAtom,
  type CustomerAttributeQueryDataAtom,
  type DataDisplayTypeAtom,
  type GroupNameMapAtom,
  type GroupsDiffsAtom,
  type HiddenColumnKeysAtom,
  type HiddenRowIdsAtom,
  type IndividualSortValueAtom,
  type IsTabulatedAtom,
  type MatrixSortValueAtom,
  type NameEditingGroupAtom,
  type QuestionQueryDataAtom,
  type QuestionsDiffsMapAtom,
  type ResultViewerTypeAtom,
  type SequentialSortValueAtom,
} from '../utils'

type Atoms = {
  commentAtom: CommentAtom
  customerAttributeQueryDataAtom: CustomerAttributeQueryDataAtom
  dataDisplayTypeAtom: DataDisplayTypeAtom
  groupNameMapAtom: GroupNameMapAtom
  groupsDiffsAtom: GroupsDiffsAtom
  hiddenColumnKeysAtom: HiddenColumnKeysAtom
  hiddenRowIdsAtom: HiddenRowIdsAtom
  individualSortValueAtom: IndividualSortValueAtom
  matrixSortValueAtom: MatrixSortValueAtom
  questionQueryDataAtom: QuestionQueryDataAtom
  questionsDiffsMapAtom: QuestionsDiffsMapAtom
  resultViewerTypeAtom: ResultViewerTypeAtom
  savedCommentAtom: CommentAtom
  sequentialSortValueAtom: SequentialSortValueAtom
} & {
  isTabulatedAtom: IsTabulatedAtom
  nameEditingGroupAtom: NameEditingGroupAtom
}

const AtomsContext = React.createContext<Atoms>(undefined as unknown as Atoms)

export type AtomsProviderProps = React.PropsWithChildren<{
  atoms: Atoms
}>

export const AtomsProvider = ({ atoms, children }: AtomsProviderProps) => (
  <AtomsContext.Provider value={atoms}>{children}</AtomsContext.Provider>
)

type Shorten<T extends string> = T extends `${infer U}Atom` ? U : never
type AtomName = Shorten<keyof Atoms>
type WriteableAtomName = Exclude<
  AtomName,
  'customerAttributeQueryData' | 'isTabulated' | 'questionQueryData'
>
const useAnAtomContext = <T extends AtomName>(name: T) => {
  const atomsContext = React.useContext(AtomsContext)
  const atom = React.useMemo(
    () => atomsContext[`${name}Atom`],
    [atomsContext, name],
  )

  return atom
}

export const useAtomContext = <T extends WriteableAtomName>(name: T) => {
  const atom = useAnAtomContext(name)

  return useAtom(atom)
}

export const useAtomValueContext = <T extends AtomName>(name: T) => {
  const atom = useAnAtomContext(name)

  return useAtomValue(atom)
}

export const useSetAtomContext = <T extends WriteableAtomName>(name: T) => {
  const atom = useAnAtomContext(name)

  return useSetAtom(atom)
}

export const useCommentContext = (
  key: string,
): [string, (comment: string) => void] => {
  const commentAtom = useAnAtomContext('comment')
  const [record, setRecord] = useAtom(commentAtom)
  const comment = record[key] ?? ''
  const setComment = React.useCallback(
    (next: string) => setRecord((prev) => ({ ...prev, [key]: next })),
    [setRecord, key],
  )

  return [comment, setComment]
}

export const useSavedCommentContext = (
  key: string,
): [string, (comment: string) => void] => {
  const savedCommentAtom = useAnAtomContext('savedComment')
  const [record, setRecord] = useAtom(savedCommentAtom)
  const savedComment = record[key] ?? ''
  const setSavedComment = React.useCallback(
    (next: string) => setRecord((prev) => ({ ...prev, [key]: next })),
    [setRecord, key],
  )

  return [savedComment, setSavedComment]
}
